import React, { useState } from 'react';
import ReactMarkdown from 'markdown-to-jsx';
import PropTypes from 'prop-types';
import BEMHelper from '@ynap/bem-helper';
import { linkRenderer } from '@ynap/router';
import { ValueTransition } from '@ynap/value-transition';

const bem = new BEMHelper('Header');

const Header = (props) => {
    const [showFullCopy, setShowFullCopy] = useState(false);

    const {
        banner,
        children,
        className = {},
        copy,
        hasImage: showMax3Lines,
        // eslint-disable-next-line @typescript-eslint/naming-convention
        internal_urls = [],
        loading,
        locale,
        messages,
        preTitle,
        saleTitle = '',
        subTitle,
        title = '',
        urlConfig = {},
    } = props;
    const { containerClass, contentClass, contentModifier = [], textClass } = className;

    const hideCopy = copy ? '' : 'hide';
    const components = { overrides: { a: ({ children, href }) => linkRenderer(urlConfig, internal_urls, locale, href, children) } };

    const hideTitleModifier = preTitle && !title ? 'hide' : '';
    // sale PLP already has SALE as the title so we add this modifier in order to
    // hide the duplicate SALE and fix the title and subtitle colours
    const salePageModifier = saleTitle.toLowerCase() === title.toLowerCase() ? 'sale' : '';

    const subTitleModifer = subTitle ? 'grey' : 'large';

    const titleValue = saleTitle + title;
    return (
        <div className={bem(null, { loading }, containerClass)}>
            <div className={bem('content', { loading }, contentClass)}>
                {preTitle && (
                    <ValueTransition
                        animationName="fadeOut"
                        classAnimationModifier={bem('pretitle', 'changing')}
                        className={bem('pretitle', contentModifier, textClass)}
                        component="p"
                    >
                        {preTitle}
                    </ValueTransition>
                )}
                <div className={bem('headingsContainer')}>
                    <ValueTransition
                        animationName="fadeOut"
                        classAnimationModifier={bem('title', 'changing')}
                        className={bem('title', [hideTitleModifier, salePageModifier, subTitleModifer, ...contentModifier], textClass)}
                        component={subTitle ? 'h2' : 'h1'}
                        value={titleValue}
                    >
                        {saleTitle && saleTitle.toLowerCase() !== title.toLowerCase() && <span className={bem('sale')}>{saleTitle}&nbsp;</span>}
                        {title}
                    </ValueTransition>

                    <ValueTransition
                        animationName="fadeOut"
                        classAnimationModifier={bem('subtitle', 'changing')}
                        className={bem('subtitle', [salePageModifier, ...contentModifier], textClass)}
                        component={subTitle ? 'h1' : 'h2'}
                    >
                        {subTitle}
                    </ValueTransition>

                    {/*
                        MRP PLP banner rules:
                            Banners without image  - truncate copy at 2 lines with '...more' button to show the rest
                            Banners with image     - truncate copy at 3 lines with '...more' button (only occurs on desktop)
                    */}
                </div>
                <ValueTransition
                    animationName="fadeOut"
                    classAnimationModifier={bem('copy', 'changing')}
                    className={bem('copy', [hideCopy, (showFullCopy && 'showFullCopy') || (showMax3Lines && 'showMax3Lines'), ...contentModifier], textClass)}
                    component="div"
                    value={copy}
                >
                    <ReactMarkdown
                        options={{
                            forceBlock: true,
                            forceWrapper: true,
                            wrapper: React.Fragment,
                            ...(!hideCopy ? components : {}),
                        }}
                    >
                        {copy}
                    </ReactMarkdown>
                    <div
                        className={bem('moreButton', [showMax3Lines && 'showMax3Lines', ...contentModifier], 'moreButton')}
                        onClick={() => setShowFullCopy(true)}
                    >
                        {messages?.viewMoreCopy?.() || ''}
                    </div>
                </ValueTransition>
                {children}
            </div>
            {banner}
        </div>
    );
};

Header.defaultProps = {
    title: '',
    messages: '',
    subTitle: '',
    copy: '',
};

Header.propTypes = {
    title: PropTypes.string.isRequired,
    subTitle: PropTypes.string.isRequired,
    preTitle: PropTypes.string.isRequired,
    copy: PropTypes.number.isRequired,
    className: PropTypes.string,
    loading: PropTypes.bool,
    banner: PropTypes.element,
    children: PropTypes.node,
    hideCopy: PropTypes.bool,
    messages: PropTypes.shape({
        search: PropTypes.string,
    }),
    locale: PropTypes.string,
    urlConfig: PropTypes.object,
    internal_urls: PropTypes.object,
    saleTitle: PropTypes.string,
};

export default Header;
